import React, { useState, useEffect } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import EnquiryModal from './EnquiryModal';
import RequestAppraisalModal from './RequestAppraisalModal';
import { computeBranding } from '../utils/useAgentDetails';

const AgentAuthorRow = ({ agentDetails, showDivider = true, align = 'left', showContactButton = true, showAppraisalReview = false, propertyAddress = '', propertyDetails = null, onReportClick = null, authorOverride = null }) => {
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [showAppraisalModal, setShowAppraisalModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { accentColor, buttonTextColor, isLight } = computeBranding(agentDetails) || {};

  if (!agentDetails) return null;

  // Hide for home-price or when no landing_page_slug is defined, unless article author override is provided
  const slug = agentDetails.landing_page_slug;
  if ((!slug || slug === 'home-price') && !authorOverride) return null;

  const displayName = authorOverride?.name || agentDetails.agent_name;
  const displayImage = authorOverride?.image || agentDetails.agent_profile_image;
  const displayTitle = authorOverride?.title || agentDetails.agent_title;
  const displayAgency = authorOverride?.agency || agentDetails.agency_name_short;

  // Three layout modes:
  // 'center'      → stacked vertically, centered (landing pages, news list)
  // 'center-flat' → flat inline, centered horizontally (property profile)
  // 'left'        → flat inline, left-aligned (news articles)
  const isStacked = align === 'center';
  const isFlat = align === 'left' || align === 'center-flat';
  const flatJustify = align === 'center-flat' ? 'center' : 'flex-start';

  const contactButtons = showContactButton && (
    <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: isStacked ? 'center' : 'flex-start' }}>
      {onReportClick && (
        <Button
          variant="primary"
          size="sm"
          onClick={onReportClick}
          style={{
            backgroundColor: accentColor,
            borderColor: isLight ? '#222' : accentColor,
            color: buttonTextColor,
            borderRadius: 8,
            fontWeight: 600,
            fontSize: isMobile ? '0.75rem' : '0.8rem',
            padding: '5px 12px',
          }}
        >
          Free Instant Property Report
        </Button>
      )}
      {showAppraisalReview && (
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowAppraisalModal(true)}
          style={{
            backgroundColor: accentColor,
            borderColor: isLight ? '#222' : accentColor,
            color: buttonTextColor,
            borderRadius: 8,
            fontWeight: 600,
            fontSize: isMobile ? '0.75rem' : '0.8rem',
            padding: '5px 12px',
          }}
        >
          {isMobile ? 'Request Review' : 'Request Appraisal Review'}
        </Button>
      )}
      <Button
        variant={showAppraisalReview || onReportClick ? 'outline-secondary' : 'primary'}
        size="sm"
        onClick={() => setShowEnquiry(true)}
        style={showAppraisalReview || onReportClick ? { fontSize: isMobile ? '0.75rem' : '0.8rem', borderRadius: 8, fontWeight: 500, padding: '5px 12px' } : {
          backgroundColor: accentColor,
          borderColor: isLight ? '#222' : accentColor,
          color: buttonTextColor,
          borderRadius: 8,
          fontWeight: 600,
          fontSize: isMobile ? '0.75rem' : '0.8rem',
          padding: '5px 12px',
        }}
      >
        {isMobile ? 'Contact' : 'Contact Agent'}
      </Button>
    </div>
  );

  return (
    <>
      {isStacked ? (
        /* ── Centered / stacked layout for landing pages ── */
        <div style={{ marginTop: 12, marginBottom: 12, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          {displayImage && (
            <img
              src={displayImage}
              alt={displayName}
              style={{
                width: isMobile ? 44 : 56,
                height: isMobile ? 44 : 56,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #f3f4f6',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                marginBottom: 8,
              }}
            />
          )}
          <div style={{ fontWeight: 700, fontSize: isMobile ? '0.88rem' : '1rem', color: '#111', letterSpacing: '-0.01em', marginBottom: 2 }}>
            {displayName}
          </div>
          <div style={{ color: '#6b7280', fontWeight: 400, fontSize: isMobile ? '0.78rem' : '0.85rem', whiteSpace: 'nowrap' }}>
            {!isMobile && displayTitle
              ? `${displayAgency} - ${displayTitle}`
              : displayAgency}
          </div>
          {contactButtons}
        </div>
      ) : (
        /* ── Flat / inline layout ── */
        <Row style={{ marginTop: 16, marginBottom: 16, width: '100%', justifyContent: flatJustify, marginLeft: 0, marginRight: 0 }}>
          <Col
            xs="auto"
            style={{
              display: 'flex',
              alignItems: 'center',
              textAlign: 'left',
              paddingLeft: 0,
              paddingRight: 0,
            }}
          >
            {displayImage && (
              <img
                src={displayImage}
                alt={displayName}
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #f3f4f6',
                  marginRight: 14,
                  flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}
              />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'left' }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 2, color: '#111', letterSpacing: '-0.01em' }}>
                {displayName}
              </div>
              <div style={{ color: '#6b7280', fontWeight: 400, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                {!isMobile && displayTitle
                  ? `${displayAgency} - ${displayTitle}`
                  : displayAgency}
              </div>
              {contactButtons}
            </div>
          </Col>
        </Row>
      )}
      {showDivider && (
        <hr
          style={{
            border: 'none',
            borderTop: '1px solid #e5e7eb',
            margin: '8px 0 16px 0',
            width: '100%',
          }}
        />
      )}
      <EnquiryModal
        show={showEnquiry}
        onHide={() => setShowEnquiry(false)}
        agentDetails={agentDetails}
      />
      {showAppraisalReview && (
        <RequestAppraisalModal
          show={showAppraisalModal}
          onHide={() => setShowAppraisalModal(false)}
          agentDetails={agentDetails}
          propertyAddress={propertyAddress}
          propertyDetails={propertyDetails}
        />
      )}
    </>
  );
};

export default AgentAuthorRow;
